/**
 * L'anteprima del prodotto nella schermata di accesso.
 *
 * Quattro schermate della piattaforma — la chat con una proposta, gli
 * immobili, i conti, gli avvisi — disegnate **a grandezza vera**, con gli
 * stessi caratteri e gli stessi spazi dell'app, e poi rimpicciolite per stare
 * nel pannello. Disegnarle già piccole le faceva sembrare un'illustrazione;
 * così sembrano l'app, perché lo sono quasi.
 *
 * Scorrono da sole. L'avanzamento si calcola a ogni fotogramma invece che con
 * un'animazione CSS: la barra è fluida, e cambiare scena a mano non lascia
 * barre a metà.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowUp,
  Bell,
  Building2,
  CalendarDays,
  Check,
  Euro,
  History,
  Inbox,
  Landmark,
  MessageSquare,
  PiggyBank,
  Plus,
  Receipt,
  Settings,
  Sparkles,
  Sun,
} from "lucide-react";
import { cx } from "../ui/primitives.tsx";

/** Quanto resta a schermo ogni scena. */
const DURATA_MS = 4_200;

/** La misura a cui le schermate sono disegnate: quella di un portatile. */
const LARGHEZZA = 1180;
const ALTEZZA = 740;
/**
 * Dove il pannello taglia la finestra, in pixel del disegno. Tutte le scritte
 * stanno a sinistra di questa linea (con un margine): oltre, la finestra
 * continua vuota o con sole icone, così il taglio non spezza mai una parola.
 * Se sposti qualcosa verso destra, controlla che resti prima di qui.
 */
const TAGLIO = 1060;

const SCENE = [
  {
    id: "chat",
    titolo: "Chiedi in chat. Otto prepara la modifica.",
    testo: "Vedi cosa cambia notte per notte. Parte solo se approvi.",
  },
  {
    id: "immobili",
    titolo: "Ogni casa, a colpo d'occhio.",
    testo: "Occupazione, incasso, notti libere e i prossimi arrivi.",
  },
  {
    id: "conti",
    titolo: "Sai quanto ti resta, davvero.",
    testo: "Ricavi, commissioni dei canali, pulizie e tasse, mese per mese.",
  },
  {
    id: "avvisi",
    titolo: "Ti avvisa prima che diventi un problema.",
    testo: "Arrivi senza pulizia, ospiti in attesa, notti isolate.",
  },
] as const;

type IdScena = (typeof SCENE)[number]["id"];

export function AnteprimaProdotto() {
  const [indice, setIndice] = useState(0);
  const [avanzamento, setAvanzamento] = useState(0);
  const trascorso = useRef(0);

  // Un orologio solo, a fotogrammi. Non si ferma col mouse sopra: chi guarda
  // l'anteprima ci tiene il mouse, e la vedeva immobile.
  useEffect(() => {
    let frame = 0;
    let prima = performance.now();
    const passo = (ora: number) => {
      // Una scheda rimasta in sottofondo non deve saltare tre scene al ritorno.
      const delta = Math.min(ora - prima, 100);
      prima = ora;
      trascorso.current += delta;
      if (trascorso.current >= DURATA_MS) {
        trascorso.current = 0;
        setIndice((i) => (i + 1) % SCENE.length);
      }
      setAvanzamento(trascorso.current / DURATA_MS);
      frame = requestAnimationFrame(passo);
    };
    frame = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(frame);
  }, []);

  const vai = useCallback((i: number) => {
    trascorso.current = 0;
    setAvanzamento(0);
    setIndice(i);
  }, []);

  const scena = SCENE[indice]!;

  return (
    <aside className="relative flex w-full flex-col overflow-hidden rounded-[22px] bg-nav text-nav-ink">
      {/* Fondo: righe diagonali sottili e due aloni, fermi. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(115deg, hsl(220 14% 70% / 0.06) 0 1px, transparent 1px 16px)",
          maskImage:
            "radial-gradient(ellipse 85% 70% at 50% 40%, black 25%, transparent 80%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 size-[560px] rounded-full bg-[hsl(222_45%_34%/0.35)] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-24 size-[460px] rounded-full bg-[hsl(160_30%_30%/0.16)] blur-3xl"
      />

      {/* La finestra, a grandezza vera e poi in scala */}
      <div className="relative flex min-h-0 flex-1 pl-8 pt-10 xl:pl-10">
        <InScala>
          <Browser>
            {SCENE.map((s, i) => (
              <div
                key={s.id}
                aria-hidden={i !== indice}
                className="absolute inset-0 transition-[opacity,transform] duration-500 ease-out"
                style={{
                  opacity: i === indice ? 1 : 0,
                  transform: i === indice ? "none" : "scale(0.992)",
                }}
              >
                <Scena id={s.id} />
              </div>
            ))}
          </Browser>
        </InScala>
      </div>

      {/* Il testo e le barre */}
      <div className="relative px-10 pb-9 pt-7 text-center">
        <div className="relative h-[58px]">
          {SCENE.map((s, i) => (
            <div
              key={s.id}
              aria-hidden={i !== indice}
              className="absolute inset-x-0 top-0 transition-[opacity,transform] duration-500 ease-out"
              style={{
                opacity: i === indice ? 1 : 0,
                transform: i === indice ? "none" : "translateY(6px)",
              }}
            >
              <p className="text-[21px] font-bold leading-snug tracking-tight text-ink-inverse">
                {s.titolo}
              </p>
              <p className="mx-auto mt-1 max-w-[440px] text-[14px] leading-relaxed">
                {s.testo}
              </p>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-6 flex max-w-[260px] gap-1.5">
          {SCENE.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Mostra: ${s.titolo}`}
              aria-current={i === indice}
              onClick={() => vai(i)}
              className="h-1 flex-1 overflow-hidden rounded-pill bg-nav-ink/25"
            >
              <span
                className="block h-full rounded-pill bg-ink-inverse"
                style={{
                  width: `${
                    i < indice ? 100 : i === indice ? avanzamento * 100 : 0
                  }%`,
                }}
              />
            </button>
          ))}
        </div>
      </div>
      <span className="sr-only" aria-live="polite">
        {scena.titolo}
      </span>
    </aside>
  );
}

// ── La scala ────────────────────────────────────────────────────────────────

/**
 * Rimpicciolisce il contenuto disegnato a LARGHEZZA×ALTEZZA per stare dove c'è.
 * In larghezza si vede fino a TAGLIO, e il resto esce dal pannello: una
 * schermata appoggiata sul bordo, tagliata dove non ci sono scritte.
 */
function InScala({ children }: { children: ReactNode }) {
  const scatola = useRef<HTMLDivElement>(null);
  const [scala, setScala] = useState(0.55);

  useLayoutEffect(() => {
    const el = scatola.current;
    if (!el) return;
    const misura = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      setScala(Math.min(width / TAGLIO, height / ALTEZZA, 1));
    };
    misura();
    if (typeof ResizeObserver === "undefined") return;
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(el);
    return () => osservatore.disconnect();
  }, []);

  return (
    <div ref={scatola} className="relative h-full min-h-[300px] w-full">
      <div
        className="absolute left-0 top-1/2 origin-left"
        style={{
          width: LARGHEZZA,
          height: ALTEZZA,
          transform: `translateY(-50%) scale(${scala})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Browser({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex size-full flex-col overflow-hidden rounded-[14px] bg-surface text-ink"
      style={{
        boxShadow:
          "0 60px 120px -40px rgb(0 0 0 / 0.8), 0 0 0 1px rgb(255 255 255 / 0.06)",
      }}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-surface-muted px-5 py-3">
        <span className="size-3 rounded-full bg-[#ff5f57]" />
        <span className="size-3 rounded-full bg-[#febc2e]" />
        <span className="size-3 rounded-full bg-[#28c840]" />
        <span className="ml-[240px] rounded-pill bg-surface px-24 py-1 text-[12px] text-ink-faint ring-1 ring-line">
          app.otto.it
        </span>
      </div>
      <div className="relative min-h-0 flex-1">{children}</div>
    </div>
  );
}

function Scena({ id }: { id: IdScena }) {
  if (id === "chat") return <ScenaChat />;
  if (id === "immobili") return <ScenaImmobili />;
  if (id === "conti") return <ScenaConti />;
  return <ScenaAvvisi />;
}

// ── La colonna scura, come nell'app ─────────────────────────────────────────

function Nav({ attiva }: { attiva: "chat" | "avvisi" }) {
  const voci = [
    { id: "chat", Icona: MessageSquare, label: "Chat", badge: 0 },
    { id: "avvisi", Icona: Bell, label: "Avvisi", badge: 3 },
    { id: "impostazioni", Icona: Settings, label: "Impostazioni", badge: 0 },
  ];
  return (
    <div className="flex h-full w-[220px] shrink-0 flex-col bg-nav px-3 py-5 text-nav-ink">
      <div className="mb-6 flex items-center gap-2.5 px-3">
        <svg viewBox="0 0 32 32" className="size-7" aria-hidden>
          <path
            d="M16 3.5 4.5 12.2v13.4c0 1.6 1.3 2.9 2.9 2.9h17.2c1.6 0 2.9-1.3 2.9-2.9V12.2L16 3.5Z"
            fill="white"
          />
          <path
            d="M12.2 17.4h7.6M12.2 21.2h4.8"
            stroke="var(--color-nav)"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
        <span className="text-[18px] font-extrabold text-ink-inverse">
          Otto
        </span>
      </div>
      {voci.map(({ id, Icona, label, badge }) => (
        <span
          key={id}
          className={cx(
            "flex items-center gap-3 rounded-card px-3 py-2.5 text-[14px] font-semibold",
            id === attiva && "bg-nav-soft text-ink-inverse",
          )}
        >
          <Icona size={18} />
          <span className="flex-1">{label}</span>
          {badge > 0 ? (
            <span className="grid size-5 place-items-center rounded-full bg-ink-inverse text-[11px] font-bold text-nav">
              {badge}
            </span>
          ) : null}
        </span>
      ))}
      <div className="mt-6 flex items-center justify-between px-3 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-nav-ink/50">
          Conversazioni
        </span>
        <Plus size={15} />
      </div>
      {[
        "Prezzi di ottobre a Buniva",
        "Chi arriva domani?",
        "Quanto ho guadagnato ad agosto",
        "Risposta a Flavio",
      ].map((t, i) => (
        <span
          key={t}
          className={cx(
            "truncate rounded-card px-3 py-2 text-[13px]",
            i === 0 && attiva === "chat" && "bg-nav-soft text-ink-inverse",
          )}
        >
          {t}
        </span>
      ))}
      <div className="mt-auto flex items-center gap-2.5 rounded-card-lg bg-nav-soft p-2">
        <span className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[13px] font-extrabold text-white">
          GC
        </span>
        <span className="text-[13.5px] font-bold text-ink-inverse">
          Giulia Conti
        </span>
      </div>
    </div>
  );
}

/** Le scorciatoie a destra della chat. */
function Scorciatoie({ attiva }: { attiva?: string }) {
  const voci = [
    { id: "oggi", Icona: Sun },
    { id: "messaggi", Icona: Inbox },
    { id: "immobili", Icona: Building2 },
    { id: "consigli", Icona: Sparkles },
    { id: "storico", Icona: History },
  ];
  return (
    <div className="flex w-[60px] shrink-0 flex-col items-center gap-2 border-l border-line pt-5">
      {voci.map(({ id, Icona }) => (
        <span
          key={id}
          className={cx(
            "grid size-10 place-items-center rounded-field",
            id === attiva ? "bg-ink text-ink-inverse" : "text-ink-soft",
          )}
        >
          <Icona size={18} />
        </span>
      ))}
    </div>
  );
}

// ── 1. La chat ──────────────────────────────────────────────────────────────

function ScenaChat() {
  return (
    <div className="flex h-full">
      <Nav attiva="chat" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-end border-b border-line px-10 py-3.5">
          <span className="grid size-10 place-items-center rounded-full bg-surface-muted text-ink-soft">
            <Bell size={17} />
          </span>
        </div>

        <div className="flex min-h-0 w-[780px] flex-1 flex-col gap-4 px-10 py-6">
          <div className="ml-auto max-w-[420px] rounded-card-lg rounded-br-md bg-ink px-4 py-2.5 text-[14.5px] text-ink-inverse">
            Alza del 10% Buniva dal 20 al 22 ottobre
          </div>
          <div className="flex gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-muted">
              <Sparkles size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14.5px] leading-relaxed">
                Ecco la proposta. Il 22 è sabato: parto da un prezzo più alto,
                quindi l'aumento lì si vede di più.
              </p>
              <div className="mt-3 rounded-card-lg border border-line bg-surface">
                <div className="flex items-center gap-3 border-b border-line px-4 py-3">
                  <span className="size-10 rounded-field bg-gradient-to-br from-[#e8c6a8] to-[#b88a63]" />
                  <span className="flex-1">
                    <span className="block text-[14.5px] font-bold">
                      Buniva · prezzo notte
                    </span>
                    <span className="block text-[12.5px] text-ink-soft">
                      Torino · 3 notti
                    </span>
                  </span>
                  <span className="rounded-pill bg-ok-soft px-2.5 py-1 text-[12px] font-bold text-ok">
                    +38 €
                  </span>
                </div>
                <div className="flex flex-col gap-1.5 px-4 py-3">
                  {[
                    ["lun 20 ott", "120 €", "132 €"],
                    ["mar 21 ott", "120 €", "132 €"],
                    ["sab 22 ott", "135 €", "149 €"],
                  ].map(([giorno, prima, dopo]) => (
                    <div
                      key={giorno}
                      className="flex items-center justify-between rounded-field bg-surface-muted px-3.5 py-2 text-[13.5px]"
                    >
                      <span className="text-ink-soft">{giorno}</span>
                      <span className="tabular-nums">
                        <span className="text-ink-faint line-through">
                          {prima}
                        </span>
                        <span className="ml-3 font-bold">{dopo}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 border-t border-line px-4 py-3">
                  <span className="flex items-center gap-1.5 rounded-pill bg-ink px-5 py-2 text-[13.5px] font-bold text-ink-inverse">
                    <Check size={15} /> Approva
                  </span>
                  <span className="rounded-pill px-4 py-2 text-[13.5px] font-semibold text-ink-soft ring-1 ring-line">
                    Scarta
                  </span>
                  <span className="ml-auto text-[12px] text-ink-faint">
                    Nessuna notte prenotata · sopra il minimo
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-[780px] px-10 pb-6">
          <div className="flex items-center gap-3 rounded-pill border border-line bg-surface px-5 py-3 shadow-soft">
            <span className="flex-1 text-[14px] text-ink-faint">
              Scrivi un messaggio…
            </span>
            <span className="grid size-9 place-items-center rounded-full bg-ink text-ink-inverse">
              <ArrowUp size={17} />
            </span>
          </div>
        </div>
      </div>
      <Scorciatoie />
    </div>
  );
}

// ── 2. Gli immobili ─────────────────────────────────────────────────────────

const CASE = [
  {
    nome: "Buniva",
    occ: 72,
    prezzo: "148 €",
    colore: "from-[#e8c6a8] to-[#b88a63]",
  },
  {
    nome: "San Maurizio",
    occ: 43,
    prezzo: "208 €",
    colore: "from-[#b9c7d9] to-[#7f96b3]",
  },
  {
    nome: "Santa Giulia",
    occ: 93,
    prezzo: "89 €",
    colore: "from-[#c9d8c4] to-[#8ea884]",
  },
  {
    nome: "Sanremo Mare",
    occ: 58,
    prezzo: "132 €",
    colore: "from-[#d9c9e2] to-[#a48cb5]",
  },
];

function Kpi({
  Icona,
  etichetta,
  valore,
  sotto,
  delta,
  evidenza,
}: {
  Icona: typeof Euro;
  etichetta: string;
  valore: string;
  sotto: string;
  delta?: { testo: string; buono: boolean };
  evidenza?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-card border p-4",
        evidenza ? "border-ink bg-surface-muted" : "border-line",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="grid size-9 place-items-center rounded-field bg-surface-muted text-ink-soft">
          <Icona size={17} />
        </span>
        {delta ? (
          <span
            className={cx(
              "rounded-pill px-2 py-0.5 text-[11.5px] font-bold",
              delta.buono ? "bg-ok-soft text-ok" : "bg-danger-soft text-danger",
            )}
          >
            {delta.testo}
          </span>
        ) : null}
      </div>
      <p className="mt-3 text-[11.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">
        {etichetta}
      </p>
      <p className="mt-1 whitespace-nowrap text-[24px] font-extrabold leading-none tabular-nums">
        {valore}
      </p>
      <p className="mt-1.5 text-[12.5px] text-ink-soft">{sotto}</p>
    </div>
  );
}

function ScenaImmobili() {
  const notti = Array.from({ length: 30 }, (_, i) =>
    [2, 3, 12, 13, 21, 22, 23, 24].includes(i)
      ? "libera"
      : i === 17
        ? "bloccata"
        : "venduta",
  );
  const schede: Array<[string, typeof Euro]> = [
    ["Calendario", CalendarDays],
    ["Conti", Euro],
    ["Annuncio", Receipt],
  ];
  return (
    <div className="flex h-full">
      <Nav attiva="chat" />
      <div className="flex min-w-0 flex-1">
        {/* L'elenco */}
        <div className="w-[270px] shrink-0 border-r border-line px-3 py-5">
          <div className="mx-2 mb-4 rounded-pill bg-surface-muted px-4 py-2.5 text-[13px] text-ink-faint">
            Cerca per nome o città…
          </div>
          <p className="px-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            Torino · 3
          </p>
          {CASE.map((c, i) => (
            <div
              key={c.nome}
              className={cx(
                "flex items-center gap-3 rounded-card-lg p-2.5",
                i === 0 && "bg-surface-muted",
              )}
            >
              <span
                className={cx(
                  "size-11 shrink-0 rounded-card bg-gradient-to-br",
                  c.colore,
                )}
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between">
                  <span className="text-[14px] font-bold">{c.nome}</span>
                  <span className="text-[11px] text-ink-faint">{c.prezzo}</span>
                </span>
                <span className="mt-1 flex items-center gap-2 text-[12.5px] text-ink-soft">
                  <span className="h-1.5 w-14 overflow-hidden rounded-pill bg-surface-sunken">
                    <span
                      className={cx(
                        "block h-full rounded-pill",
                        c.occ >= 70 ? "bg-ok" : "bg-ink/60",
                      )}
                      style={{ width: `${c.occ}%` }}
                    />
                  </span>
                  {c.occ}%
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* La scheda */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-3 border-b border-line px-6 py-4">
            <span className="size-11 rounded-card bg-gradient-to-br from-[#e8c6a8] to-[#b88a63]" />
            <span>
              <span className="block text-[16px] font-extrabold">Buniva</span>
              <span className="block text-[13px] text-ink-soft">
                Torino · 4 posti letto
              </span>
            </span>
            {["airbnb", "booking"].map((c, i) => (
              <span
                key={c}
                className={cx(
                  "rounded-pill bg-surface-muted px-2.5 py-1 text-[11.5px] font-bold text-ink-soft",
                  i === 0 && "ml-3",
                )}
              >
                {c}
              </span>
            ))}
          </div>
          <div className="flex gap-1.5 border-b border-line px-6 py-3.5">
            {schede.map(([label, Icona], i) => (
              <span
                key={label}
                className={cx(
                  "flex items-center gap-2 rounded-pill px-4 py-2 text-[13.5px] font-bold",
                  i === 0 ? "bg-ink text-ink-inverse" : "text-ink-soft",
                )}
              >
                <Icona size={15} />
                {label}
              </span>
            ))}
          </div>
          <div className="w-[560px] px-6 py-5">
            <div className="grid grid-cols-3 gap-3">
              <Kpi
                Icona={CalendarDays}
                etichetta="Occupazione"
                valore="72%"
                sotto="nel mese in corso"
              />
              <Kpi
                Icona={Euro}
                etichetta="Incasso"
                valore="2.434 €"
                sotto="dalle notti già vendute"
              />
              <Kpi
                Icona={CalendarDays}
                etichetta="Notti da vendere"
                valore="8"
                sotto="da oggi a fine mese, 1.184 €"
              />
            </div>
            <div className="mt-4 rounded-card border border-line p-4">
              <div className="flex items-center gap-4">
                <p className="text-[14px] font-extrabold">Notte per notte</p>
                <span className="flex gap-3 text-[11.5px] text-ink-soft">
                  <span className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-[3px] bg-ok" /> venduta
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-[3px] bg-surface-sunken ring-1 ring-line" />{" "}
                    libera
                  </span>
                </span>
              </div>
              <div
                className="mt-3 grid gap-1"
                style={{ gridTemplateColumns: "repeat(30, minmax(0, 1fr))" }}
              >
                {notti.map((stato, i) => (
                  <span key={i} className="flex flex-col items-center gap-1">
                    <span
                      className={cx(
                        "h-8 w-full rounded-[5px]",
                        stato === "venduta" && "bg-ok",
                        stato === "libera" &&
                          "bg-surface-sunken ring-1 ring-inset ring-line",
                        stato === "bloccata" && "bg-line-strong",
                        i === 0 && "ring-2 ring-inset ring-ink",
                      )}
                    />
                    {/* Un giorno ogni sette: tutti e trenta si toccherebbero. */}
                    <span className="h-3 text-[9.5px] tabular-nums text-ink-faint">
                      {i % 7 === 0 ? ((10 + i) % 31) + 1 : ""}
                    </span>
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {[
                ["MS", "Mara Serra", "10–11 ottobre", "in casa"],
                ["FL", "Franck Laliaux", "il 12 ottobre", ""],
              ].map(([ini, nome, quando, stato]) => (
                <div
                  key={nome}
                  className="flex items-center gap-3 rounded-field bg-surface-muted px-3.5 py-2.5"
                >
                  <span className="grid size-8 place-items-center rounded-full bg-ink text-[11px] font-bold text-ink-inverse">
                    {ini}
                  </span>
                  <span className="flex-1">
                    <span className="block text-[13.5px] font-bold">
                      {nome}
                    </span>
                    <span className="block text-[12px] text-ink-soft">
                      {quando}
                    </span>
                  </span>
                  {stato ? (
                    <span className="rounded-pill bg-ok-soft px-2 py-0.5 text-[11px] font-bold text-ok">
                      {stato}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Scorciatoie attiva="immobili" />
    </div>
  );
}

// ── 3. I conti ──────────────────────────────────────────────────────────────

function ScenaConti() {
  const righe: Array<[string, string]> = [
    ["Notti vendute", "2.434,50 €"],
    ["Booking", "−474,72 €"],
    ["IVA sulla commissione", "−96,41 €"],
    ["Pulizie", "≈ −240 €"],
    ["Imposte", "≈ −48,69 €"],
  ];
  const fette: Array<[number, string, number]> = [
    [65, "stroke-ok", 0],
    [23, "stroke-ink", 65],
    [10, "stroke-line-strong", 88],
  ];
  return (
    <div className="flex h-full">
      <Nav attiva="chat" />
      <div className="w-[820px] shrink-0 overflow-hidden px-7 py-6">
        <div className="flex items-end justify-between">
          <div>
            <p className="flex items-center gap-2 text-[16px] font-extrabold">
              <Euro size={17} /> Quanto rende · Buniva
            </p>
            <p className="mt-0.5 text-[13px] text-ink-soft">
              Ricavi, trattenute dei canali, costi: e quanto ti resta.
            </p>
          </div>
          <span className="rounded-pill bg-surface-muted px-4 py-2 text-[13.5px] font-bold">
            ‹ &nbsp;Settembre 2026&nbsp; ›
          </span>
        </div>
        <div className="mt-4 grid grid-cols-4 gap-3">
          <Kpi
            Icona={Euro}
            etichetta="Ricavi"
            valore="2.434 €"
            sotto="pagato dagli ospiti"
            delta={{ testo: "+33%", buono: true }}
          />
          <Kpi
            Icona={Landmark}
            etichetta="Canali"
            valore="571 €"
            sotto="commissioni, incasso, IVA"
            delta={{ testo: "+36%", buono: false }}
          />
          <Kpi
            Icona={Receipt}
            etichetta="Costi"
            valore="288 €"
            sotto="pulizie, imposte, spese"
            delta={{ testo: "−14%", buono: true }}
          />
          <Kpi
            Icona={PiggyBank}
            etichetta="Ti resta"
            valore="1.574 €"
            sotto="dopo canali e costi"
            delta={{ testo: "+47%", buono: true }}
            evidenza
          />
        </div>
        <div className="mt-4 grid grid-cols-[1.35fr_1fr] gap-3">
          <div className="rounded-card border border-line p-5">
            <p className="text-[14px] font-extrabold">
              Rendiconto di settembre
            </p>
            <div className="mt-3 flex flex-col">
              {righe.map(([l, v]) => (
                <div
                  key={l}
                  className="flex justify-between border-b border-line py-2 text-[13.5px] last:border-0"
                >
                  <span className="text-ink-soft">{l}</span>
                  <span className="tabular-nums">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between rounded-card bg-ink px-4 py-3 text-ink-inverse">
              <span className="text-[14px] font-extrabold">Ti resta</span>
              <span className="text-[22px] font-extrabold tabular-nums">
                1.574,68 €
              </span>
            </div>
          </div>
          <div className="rounded-card border border-line p-5">
            <p className="text-[14px] font-extrabold">Dove vanno i ricavi</p>
            <div className="mt-4 flex items-center gap-4">
              <svg
                viewBox="0 0 100 100"
                className="size-[120px] shrink-0 -rotate-90"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  strokeWidth="14"
                  className="stroke-surface-sunken"
                />
                {fette.map(([quota, classe, da]) => (
                  <circle
                    key={classe}
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    strokeWidth="14"
                    className={classe}
                    strokeDasharray={`${(quota / 100) * 251.3 - 1.5} 251.3`}
                    strokeDashoffset={-(da / 100) * 251.3}
                  />
                ))}
              </svg>
              <div className="flex flex-1 flex-col gap-2 text-[13px]">
                {[
                  ["bg-ok", "Ti resta", "65%"],
                  ["bg-ink", "Canali", "23%"],
                  ["bg-line-strong", "Pulizie", "10%"],
                ].map(([c, l, q]) => (
                  <span key={l} className="flex items-center gap-2">
                    <span className={cx("size-2.5 rounded-full", c)} />
                    <span className="flex-1">{l}</span>
                    <span className="font-bold tabular-nums">{q}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1" />
      <Scorciatoie attiva="immobili" />
    </div>
  );
}

// ── 4. Gli avvisi ───────────────────────────────────────────────────────────

function ScenaAvvisi() {
  const avvisi = [
    {
      punto: "bg-danger",
      etichetta: "pulizia mancante",
      tono: "bg-danger-soft text-danger",
      titolo: "Arrivo a Santa Giulia senza pulizia",
      testo:
        "Francesco Serra entra domani alle 15 e non c'è nessuna pulizia in programma.",
      azioni: ["Metti in programma la pulizia", "Apri l'immobile"],
    },
    {
      punto: "bg-warn",
      etichetta: "ospite in attesa",
      tono: "bg-warn-soft text-warn",
      titolo: "Flavio aspetta da 14 ore",
      testo: "«Buonasera, possiamo dormire in tre stanze separate?»",
      azioni: ["Fai scrivere la risposta", "Apri la conversazione"],
    },
    {
      punto: "bg-line-strong",
      etichetta: "notte isolata",
      tono: "bg-surface-muted text-ink-soft",
      titolo: "Una notte isolata a Buniva",
      testo:
        "Il 21 è l'unica notte libera fra due prenotazioni. Ora è a 120 €.",
      azioni: ["Rendila prenotabile", "Vedi il calendario"],
    },
  ];
  return (
    <div className="flex h-full">
      <Nav attiva="avvisi" />
      <div className="w-[820px] shrink-0 overflow-hidden px-10 py-7">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[26px] font-extrabold tracking-tight">Avvisi</p>
            <p className="mt-0.5 text-[13.5px] text-ink-soft">
              1 cosa non può aspettare.
            </p>
          </div>
          <span className="flex rounded-pill bg-surface-muted p-1 text-[13px] font-bold">
            <span className="rounded-pill bg-ink px-4 py-1.5 text-ink-inverse">
              Da vedere
            </span>
            <span className="px-4 py-1.5 text-ink-soft">Archivio</span>
          </span>
        </div>
        <div className="mt-4 flex gap-2">
          {["Tutti 8", "Ospiti 1", "Casa 2", "Ricavi 5"].map((c, i) => (
            <span
              key={c}
              className={cx(
                "rounded-pill px-3.5 py-1.5 text-[13px] font-bold",
                i === 0
                  ? "bg-ink text-ink-inverse"
                  : "bg-surface-muted text-ink-soft",
              )}
            >
              {c}
            </span>
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-3">
          {avvisi.map((a) => (
            <div key={a.titolo} className="rounded-card border border-line p-4">
              <div className="flex items-start gap-3">
                <span
                  className={cx("mt-2 size-2.5 shrink-0 rounded-full", a.punto)}
                />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-[15.5px] font-extrabold">
                    {a.titolo}
                    <span
                      className={cx(
                        "rounded-pill px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.04em]",
                        a.tono,
                      )}
                    >
                      {a.etichetta}
                    </span>
                  </p>
                  <p className="mt-1 text-[13.5px] text-ink-soft">{a.testo}</p>
                  <div className="mt-3 flex gap-2">
                    {a.azioni.map((az, i) => (
                      <span
                        key={az}
                        className={cx(
                          "rounded-pill px-3.5 py-1.5 text-[13px] font-semibold",
                          i === 0
                            ? "bg-ink text-ink-inverse"
                            : "text-ink ring-1 ring-line",
                        )}
                      >
                        {az}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
